import { getExtensionFileName } from '@vindr/utils/converter';
import { DownloadFile } from '@vindr/utils/helpers/common.helper';
import { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SelectList from '../../../../ui/SelectList';
import { useEncoder } from '../../Webcam/helper/ConvertEncoder';
import { clearListCache, modifyCacheVideo } from '../../Webcam/helper/storeIndexDB';
import CaptureManager from '../CaptureManager';
import { useCaptureManagerState, useCaptureManagerUpdater } from '../context';
import { CaptureManagerState } from '../context/type';
import MediaCardView from './MediaCardView';

const FileList = ({ modal, onSelect }: Parameters<typeof CaptureManager>['0']) => {
  const { t } = useTranslation('Vindoc');
  const { storeFiles, processingList, selected, importRef, isConverting } = useCaptureManagerState();
  const dispatch = useCaptureManagerUpdater();
  // const dispatchReport = useReportUpdater();
  const encoder = useEncoder();

  const handleSelect = useCallback(
    (e) => {
      if (!e) return;

      dispatch({ type: 'SET_SELECTED', payload: e });
      // dispatchReport?.({ type: 'SET_SELECTED_CAPTURE', payload: e });
      onSelect?.(e);
    },
    [dispatch, onSelect]
  );

  useEffect(() => {
    if (!storeFiles?.length) return;

    dispatch({
      type: 'SET_SELECTED',
      payload: storeFiles.map((x) => ({
        ...x,
        process: processingList.get(x.id),
      })),
    });
  }, [dispatch, processingList, storeFiles, storeFiles?.length]);

  const deleteInputFile = useCallback(
    (fileNameDelete: string) => {
      const attachments = Array.from(importRef.current.files);
      const fileBuffer = new DataTransfer();

      for (const file of attachments) {
        if (file.name === fileNameDelete) continue;
        fileBuffer.items.add(file);
      }

      importRef.current.files = fileBuffer.files;
    },
    [importRef]
  );

  const handleDelete = useCallback(
    (deleteFile: typeof storeFiles[number]) => {
      const deleteId = deleteFile.id;

      modal.confirm({
        title: t('Confirm Delete'),
        content: t('Are you sure you want to delete this item? This action cannot be undone.'),
        okText: t('Delete'),
        cancelText: t('Cancel'),
        okButtonProps: { type: 'primary', danger: true },
        zIndex: 10_0000,
        maskTransitionName: '',
        transitionName: '',
        onOk: async (close) => {
          //? Delete file in CaptureManagerState
          dispatch({
            type: 'DELETE_FILES',
            payload: {
              listIdSelected: [deleteId],
              newStoreFiles: storeFiles.filter((x) => x.id !== deleteId),
            },
          });

          //? Delete file in input HTML
          deleteInputFile(deleteFile?.name);

          const filterSelected = selected.filter((x) => x.id !== deleteId);

          //? Set new selected list
          dispatch({
            type: 'SET_SELECTED',
            payload: filterSelected,
          });

          //? Set new selected list to outside
          // dispatchReport?.({
          //   type: 'SET_SELECTED_CAPTURE',
          //   payload: filterSelected,
          // });
          onSelect?.(filterSelected);

          //? Delete file in indexDB
          await clearListCache([deleteFile?.name, deleteFile?.id]);
          close();
        },
      });
    },
    [modal, t, dispatch, storeFiles, deleteInputFile, selected, onSelect]
  );

  const handleDownload = useCallback(
    (file: typeof storeFiles[number]) => DownloadFile(file.file, file.name),
    []
  );

  useEffect(() => {
    if (!storeFiles?.length || !encoder.loaded) return;
    const convertList = getFileNeedConvert(storeFiles);
    if (!convertList.length) return;
    const enableCheckConvert = isConverting.enable;

    //? From now no way to run concurrently ffmpeg
    (async () => {
      if (enableCheckConvert) dispatch({ type: 'SET_CONVERTING', payload: true });
      for (const file of convertList) {
        const result = await encoder
          .convert({
            file: file.file,
            name: file.name,
            id: file.id,
          })
          .catch(console.error);

        if (!result) continue;

        const newFile = {
          ...file,
          url: result.objectURL,
          name: result.outputFileName,
          file: result.renderFile,
          type: result.type,
        };

        dispatch({
          type: 'EDIT_FILE',
          payload: newFile,
        });

        if (file.id.startsWith('backup')) {
          await modifyCacheVideo(file.id, {
            ...file,
            name: result.outputFileName,
            url: result.buffer,
            oldName: file.name,
            type: result.type,
          });
        }
      }
      if (enableCheckConvert) dispatch({ type: 'SET_CONVERTING', payload: false });
    })();
  }, [dispatch, encoder.loaded, storeFiles?.length, isConverting]);

  useEffect(() => {
    //? Set progress when converting
    if (!storeFiles?.length) return;
    const convertList = getFileNeedConvert(storeFiles);
    if (!convertList.length || !encoder?.info?.id) return;

    if (encoder?.info?.progress < 100) {
      dispatch({
        type: 'SET_PROGRESS',
        payload: {
          id: [encoder?.info?.id],
          progress: encoder?.info?.progress,
          status: 'converting',
        },
      });
    } else {
      dispatch({
        type: 'SET_PROGRESS',
        payload: {
          id: [encoder?.info?.id],
          progress: 0,
          status: 'ready',
        },
      });
    }
  }, [encoder?.info]);

  if (!storeFiles?.length) return null;

  return (
    <SelectList
      onSelect={handleSelect}
      className={`select-list mt-6 p-2 min-[1200px]:overflow-auto`}
    >
      {storeFiles?.map((file) => {
        if (!file) return null;

        const process = processingList.get(file.id);

        return (
          <SelectList.Item
            key={file.id}
            id={file.id}
            className="relative flex h-[125px] cursor-default items-center justify-center overflow-hidden rounded-md"
          >
            <MediaCardView
              tag={file.tag}
              url={file.url}
              name={file.name}
              process={process}
              onDelete={() => handleDelete(file)}
              onDownload={() => handleDownload(file)}
            />
          </SelectList.Item>
        );
      })}
    </SelectList>
  );
};

export default memo(FileList);

const getFileNeedConvert = (storeFiles: CaptureManagerState['storeFiles']) => {
  return storeFiles.filter(
    // (file) => file.tag === 'video' && getExtensionFileName(file.name) !== 'mp4'
    (file) => file.tag === 'video' && getExtensionFileName(file.name) === 'webm'
  );
};
